import { create } from 'zustand';

interface PeerMediaState {
  micEnabled: boolean;
  camEnabled: boolean;
}

interface CallStore {
  // Local states
  localMicEnabled: boolean;
  localCamEnabled: boolean;
  localStream: MediaStream | null;
  isConnectingMedia: boolean;
  callError: string | null;

  // Remote streams: peerId -> MediaStream
  remoteStreams: Record<string, MediaStream>;
  // Remote participant mic/cam states: peerId -> { micEnabled, camEnabled }
  peerMediaStates: Record<string, PeerMediaState>;

  // Actions
  setLocalMicEnabled: (enabled: boolean) => void;
  setLocalCamEnabled: (enabled: boolean) => void;
  setLocalStream: (stream: MediaStream | null) => void;
  setIsConnectingMedia: (isConnecting: boolean) => void;
  setCallError: (error: string | null) => void;
  setRemoteStream: (peerId: string, stream: MediaStream) => void;
  removeRemoteStream: (peerId: string) => void;
  setPeerMediaState: (peerId: string, state: PeerMediaState) => void;
  removePeerMediaState: (peerId: string) => void;
  resetCallStore: () => void;
}

export const useCallStore = create<CallStore>((set) => ({
  localMicEnabled: false,
  localCamEnabled: false,
  localStream: null,
  isConnectingMedia: false,
  callError: null,
  remoteStreams: {},
  peerMediaStates: {},

  setLocalMicEnabled: (enabled) => set({ localMicEnabled: enabled }),
  setLocalCamEnabled: (enabled) => set({ localCamEnabled: enabled }),
  setLocalStream: (stream) => set({ localStream: stream }),
  setIsConnectingMedia: (isConnecting) => set({ isConnectingMedia: isConnecting }),
  setCallError: (error) => set({ callError: error }),

  setRemoteStream: (peerId, stream) =>
    set((state) => ({
      remoteStreams: {
        ...state.remoteStreams,
        [peerId]: stream,
      },
    })),

  removeRemoteStream: (peerId) =>
    set((state) => {
      const updated = { ...state.remoteStreams };
      delete updated[peerId];
      return { remoteStreams: updated };
    }),

  setPeerMediaState: (peerId, mediaState) =>
    set((state) => ({
      peerMediaStates: {
        ...state.peerMediaStates,
        [peerId]: mediaState,
      },
    })),

  removePeerMediaState: (peerId) =>
    set((state) => {
      const updated = { ...state.peerMediaStates };
      delete updated[peerId];
      return { peerMediaStates: updated };
    }),

  resetCallStore: () =>
    set({
      localMicEnabled: false,
      localCamEnabled: false,
      localStream: null,
      isConnectingMedia: false,
      callError: null,
      remoteStreams: {},
      peerMediaStates: {},
    }),
}));

